import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { Divider, ListSubheader, Menu, MenuItem } from '@mui/material'
import type { EmbedReference } from '@shared/ast/astTypes'
import { inlineEmbedContent, isInlineEmbedReference, serializeEmbedReference } from '@shared/ast/embedReference'
import { $getNodeByKey, type NodeKey } from 'lexical'
import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link as RouterLink } from 'react-router-dom'

import { parseEmbedClipboard } from './embedClipboard'
import { $cardEmbedToInline, $detachEmbed, $inlineEmbedToCard, $setEmbedSource } from './embedPresentation'
import { $isCustomEmbedNode, type CustomEmbedNode } from './nodes/CustomEmbedNode'
import { $isCustomInlineEmbedNode, type CustomInlineEmbedNode } from './nodes/CustomInlineEmbedNode'
import { type ResolvedEmbed, apiService } from '../../../services/api'

interface EmbedActionsProps {
  source: EmbedReference
  nodeKey: NodeKey
  presentation: 'inline' | 'card'
  result: ResolvedEmbed | null
  failed: boolean
  bookId?: string
  isAuthor: boolean
  onOpenSource?: (filename: string, blockId: string) => void
}

/** Both presentations share source, display, and destructive actions. */
export function useEmbedActions({
  source,
  nodeKey,
  presentation,
  result,
  failed,
  bookId,
  isAuthor,
  onOpenSource,
}: EmbedActionsProps) {
  const [editor] = useLexicalComposerContext()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [busy, setBusy] = useState(false)
  const requestVersion = useRef(0)
  const afterClose = useRef<(() => void) | null>(null)
  const sourceKey = serializeEmbedReference(source)
  const editable = isAuthor && editor.isEditable()
  const content = result?.status === 'resolved' && !failed ? result.content : undefined
  const inline = isInlineEmbedReference(source) ? inlineEmbedContent(content) : undefined
  const resolvedSource = result?.source || source

  useEffect(() => {
    setBusy(false)
    return () => {
      requestVersion.current++
    }
  }, [bookId, nodeKey, sourceKey])

  const dismiss = () => {
    requestVersion.current++
    setBusy(false)
    const trigger = anchor
    afterClose.current = () => {
      if (trigger?.isConnected) {
        trigger.focus()
      }
    }
    setAnchor(null)
  }
  const closeAfterEdit = () => {
    afterClose.current = () => editor.focus()
    setAnchor(null)
  }
  const mutate = (action: (node: CustomEmbedNode | CustomInlineEmbedNode) => void) => {
    editor.update(
      () => {
        const node = $getNodeByKey(nodeKey)
        if (
          (!$isCustomEmbedNode(node) && !$isCustomInlineEmbedNode(node)) ||
          serializeEmbedReference(node.getSource()) !== sourceKey
        ) {
          toast.error('השיבוץ השתנה. פתח את התפריט מחדש.')
          return
        }
        action(node)
      },
      {
        tag: 'history-push',
        // Removing/converting this reference also unmounts its menu, so its
        // transition callback may never run. Restore the committed editor caret.
        onUpdate: () => requestAnimationFrame(() => editor.focus()),
      },
    )
    closeAfterEdit()
  }
  const changeSource = async (candidate: EmbedReference | 'clipboard') => {
    if (!bookId || busy) {
      return
    }
    const version = ++requestVersion.current
    setBusy(true)
    try {
      let next: EmbedReference
      if (candidate === 'clipboard') {
        const copied = parseEmbedClipboard(await navigator.clipboard.readText())
        if (!copied || copied.bookId !== bookId || copied.sources.length !== 1) {
          throw new Error('יש להעתיק בחירת מקור אחת מתוך הספר הזה')
        }
        next = copied.sources[0]
      } else {
        next = candidate
      }
      const response = await apiService.resolveEmbed(bookId, next)
      if (requestVersion.current !== version) {
        return
      }
      if (!response.success || response.data?.status !== 'resolved' || !response.data.content) {
        throw new Error('המקור אינו זמין. בחר טקסט והעתק אותו לשיבוץ מחדש.')
      }
      const nextContent = response.data.content
      mutate((node) => {
        if ($setEmbedSource(node, next, nextContent)) {
          toast.success('היקף המקור הזה יוצג ככרטיס')
        }
      })
    } catch (error) {
      if (requestVersion.current === version) {
        toast.error(error instanceof Error ? error.message : 'לא ניתן להחליף את המקור כעת')
      }
    } finally {
      if (requestVersion.current === version) {
        setBusy(false)
      }
    }
  }
  const open = (event: MouseEvent<HTMLElement>) => {
    if (isAuthor) {
      event.preventDefault()
      event.stopPropagation()
      setAnchor(event.currentTarget)
    }
  }
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (isAuthor && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      event.stopPropagation()
      setAnchor(event.currentTarget)
    }
  }
  const unavailable =
    source.kind === 'range'
      ? 'בחירת המקור אינה זמינה. אפשר להחליף אותה בטקסט שהועתק מחדש.'
      : 'יש להמתין למקור זמין כדי לנתק או לשנות את התצוגה'
  const menu = (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={dismiss}
      disableRestoreFocus
      TransitionProps={{
        onExited: () => {
          afterClose.current?.()
          afterClose.current = null
        },
      }}
      MenuListProps={{ 'aria-label': 'פעולות טקסט משובץ' }}
    >
      {bookId && isAuthor && (!editor.isEditable() || onOpenSource) && (
        <MenuItem
          component={RouterLink}
          to={`/edit/${bookId}/${encodeURIComponent(resolvedSource.filename)}#${resolvedSource.blockId}`}
          onClick={(event) => {
            setAnchor(null)
            if (
              onOpenSource &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey &&
              event.button === 0
            ) {
              event.preventDefault()
              onOpenSource(resolvedSource.filename, resolvedSource.blockId)
            }
          }}
        >
          פתח מקור
        </MenuItem>
      )}
      {editable && (
        <MenuItem
          disabled={busy}
          onClick={() =>
            mutate((node) => {
              node.selectNext()
              node.remove()
            })
          }
        >
          מחק
        </MenuItem>
      )}
      {editable && (
        <MenuItem
          disabled={busy || !content || (presentation === 'inline' && !inline)}
          title={!content ? unavailable : undefined}
          onClick={() => content && mutate((node) => $detachEmbed(node, content))}
        >
          נתק
        </MenuItem>
      )}
      {editable && <Divider />}
      {editable && <ListSubheader disableSticky>תצוגה</ListSubheader>}
      {editable &&
        (presentation === 'inline' ? (
          <MenuItem
            disabled={busy}
            onClick={() =>
              mutate((node) => {
                if ($isCustomInlineEmbedNode(node)) {
                  $inlineEmbedToCard(node)
                }
              })
            }
          >
            הצג ככרטיס
          </MenuItem>
        ) : (
          <MenuItem
            disabled={busy || !inline}
            title={!inline ? 'רק מקור של פסקה אחת יכול להופיע בתוך פסקה' : undefined}
            onClick={() =>
              mutate((node) => {
                if ($isCustomEmbedNode(node) && inline) {
                  $cardEmbedToInline(node, source, content?.meta.dir)
                }
              })
            }
          >
            הצג בתוך פסקה
          </MenuItem>
        ))}
      {editable && <Divider />}
      {editable && <ListSubheader disableSticky>היקף המקור</ListSubheader>}
      {editable &&
        result?.scopeOptions?.map((option) => (
          <MenuItem
            key={serializeEmbedReference(option.source)}
            disabled={busy}
            selected={serializeEmbedReference(option.source) === sourceKey}
            onClick={() => {
              void changeSource(option.source)
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      {editable && (
        <MenuItem
          disabled={busy}
          onClick={() => {
            void changeSource('clipboard')
          }}
        >
          החלף במקור שהועתק
        </MenuItem>
      )}
    </Menu>
  )
  return { menu, open, onKeyDown, expanded: Boolean(anchor) }
}
